<template>
  <div>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <div id="shop">
      <!-- Order Type Dropdown -->
      <Dropdown v-if="showOrderTypes"
                   v-model="selectedOrderType"
                   :label="$t('field.orderType')"
                   :options="orderTypeOptions"
                   :placeholder="$t('system.placeholder.chooseOption')"
                   :required="true"
      />

      <!-- Date Dropdown -->
      <Dropdown v-if="showDateOptions && dateOptions.length"
                   v-model="selectedDate"
                   :label="$t('field.date')"
                   :options="dateOptions"
                   :sort="false"
                   :placeholder="$t('system.placeholder.chooseOption')"
                   :required="true"
      />

      <!-- Time Slot Dropdown -->
      <Dropdown v-if="showTimeSlots && timeSlotOptions.length"
                   v-model="selectedTimeSlot"
                   :label="$t('field.time')"
                   :options="timeSlotOptions"
                   :placeholder="$t('system.placeholder.chooseOption')"
                   :required="true"
      />
    </div>
  </div>
</template>

<script>
import { useOrderStore } from "@/stores/order";
import { orderService } from "@/services/api/orderService";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import orderMixin from "@/components/mixins/orderMixin";
import {getOrderTypeByNumber, getOrderTypeOptionLabel} from "@/utils/orderUtils";
import {formatTimeLocale} from "@/utils/timeUtils";
import {formatDateLocale} from "@/utils/dateUtils";
import VueLoading from "vue-loading-overlay";
import {Order} from "@/constants/constants";
import Dropdown from "@/components/forms/Dropdown.vue";

const IMMEDIATE_ORDER_TIME_SLOT_VALUE = '__IMMEDIATE_ORDER__';
const IMMEDIATE_ORDER_DATE_VALUE = '__IMMEDIATE_ORDER_DATE__';

export default {
  mixins: [orderMixin],
  props: {
    selectedShop: {
      type: Object,
      required: true,
    },
  },
  components: { Dropdown: Dropdown, VueLoading },
  setup() {
    const orderStore = useOrderStore();
    const appStore = useAppStore();
    const shopStore = useShopStore();

    return { orderStore, appStore, shopStore };
  },
  data() {
    return {
      isLoading: false,
      selectedOrderType: null,
      selectedDate: null,
      selectedTimeSlot: null,
      orderTypeOptions: [],
      dateOptions: [],
      timeSlotOptions: [],
      shopObject: null,
    };
  },
  computed: {
    showOrderTypes() {
      return window.config.ORDER.shop.showOrderTypes;
    },
    showDateOptions() {
      const showForDelivery = window.config.ORDER.shop.dateOptions.showForDelivery;
      return Order.numericType.DELIVERY !== Number(this.selectedOrderType) || showForDelivery;
    },
    showTimeSlots() {
      const showForDelivery = window.config.ORDER.shop.timeSlots.showForDelivery;
      return window.config.ORDER.shop.timeSlots.enabled && (Order.numericType.DELIVERY !== Number(this.selectedOrderType) || showForDelivery);
    },
    showOnlyToTime() {
      return window.config.ORDER.shop.timeSlots.showOnlyToTime;
    },
    immediateTakeawayTimeSlotEnabled() {
      return !!window.config?.ORDER?.shop?.timeSlots?.immediateOrderOption?.enabled;
    },
  },
  emits: ['submit', 'input-change'],
  methods: {
    getImmediateTakeawayTimeSlotOption() {
      return {
        value: IMMEDIATE_ORDER_TIME_SLOT_VALUE,
        label: this.$t('orderParameters.label.timeSlotOptions.immediateOrder'),
      };
    },
    getImmediateOrderDateOption() {
      return {
        value: IMMEDIATE_ORDER_DATE_VALUE,
        label: this.$te('orderParameters.label.timeSlotOptions.immediateOrder')
          ? this.$t('orderParameters.label.timeSlotOptions.immediateOrder')
          : 'Sofortfjdkslf',
      };
    },
    syncImmediateOrderDateOption(orderType) {
      const shouldShowImmediateDate = this.immediateTakeawayTimeSlotEnabled
        && Number(orderType) === Order.numericType.TAKEAWAY;
      const hasImmediateDate = this.dateOptions.some((option) => option.value === IMMEDIATE_ORDER_DATE_VALUE);

      if (shouldShowImmediateDate && !hasImmediateDate) {
        this.dateOptions.unshift(this.getImmediateOrderDateOption());
        this.selectedDate = IMMEDIATE_ORDER_DATE_VALUE;
      }

      if (!shouldShowImmediateDate && hasImmediateDate) {
        this.dateOptions = this.dateOptions.filter((option) => option.value !== IMMEDIATE_ORDER_DATE_VALUE);

        if (this.selectedDate === IMMEDIATE_ORDER_DATE_VALUE) {
          this.selectedDate = this.dateOptions[0]?.value || null;
        }
      }
    },
    isImmediateOrderDateSelected(date) {
      return date === IMMEDIATE_ORDER_DATE_VALUE;
    },
    setImmediateTakeawayTimeSlotSelection() {
      const immediateOption = this.getImmediateTakeawayTimeSlotOption();
      this.timeSlotOptions = [immediateOption];
      this.selectedTimeSlot = immediateOption.value;
    },
    isImmediateTakeawayTimeSlotSelected(selectedSlot) {
      return selectedSlot?.value === IMMEDIATE_ORDER_TIME_SLOT_VALUE;
    },
    async startImmediateTakeawayOrderFlow() {
      const shopCode = this.shopStore.wondCode || this.selectedShop?.custom2;
      if (!shopCode) {
        this.makeToast('danger', this.$t('notification.failedGeneric'));
        return;
      }

      await this.startImmediateOrderFlow(shopCode, {
        orderType: Order.type.TAKEAWAY,
        numericOrderType: Order.numericType.TAKEAWAY,
      });
    },
    async fetchTimeSlots() {
      this.isLoading = true;
      try {
        const result = await orderService.getTimeSlots();

        if (!result.availabilityList || !result.orderTypes) {
          this.makeToast("danger", this.$t("notification.noTimeslotsAvailable"));
          return;
        }

        // Map and set order types
        this.orderTypeOptions = result.orderTypes
          .filter((entry) => entry.name !== "none")
          .map((entry) => ({
            value: entry.code,
            label: getOrderTypeOptionLabel(entry, this.$t.bind(this), this.$te?.bind(this)),
          }));

        // Preselect the first order type
        if (this.orderTypeOptions.length > 0) {
          this.selectedOrderType = this.orderTypeOptions[0].value;
        }

        if (result.skipShopSelection) {
          const selectedOrderType = getOrderTypeByNumber(this.selectedOrderType);
          this.appStore.setCurrentOrderMenuOrderType(selectedOrderType);
          this.$navigateToOrderMenuPlanner();
          return;
        }

        // Map and set available dates with formatted display text
        let availableDates = result.availabilityList.filter((entry) => !entry.closed);
        // Filter out first N dates if blockingDays is enabled
        const blockingDaysCfg = window.config?.ORDER?.shop?.dateOptions?.blockingDays;
        if (blockingDaysCfg?.enabled && Number(blockingDaysCfg.days) > 0) {
          availableDates = availableDates.slice(Number(blockingDaysCfg.days));
        }

        this.dateOptions = availableDates
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map((entry) => ({
          value: entry.date,
          label: formatDateLocale(entry.date),
        }));

        this.syncImmediateOrderDateOption(this.selectedOrderType);

        // Preselect the first order type
        if (this.dateOptions.length > 0) {
          this.selectedDate = this.dateOptions[0].value;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchTimeSlotOccupancies(orderType, date) {
      if (this.isImmediateOrderDateSelected(date)
        && this.immediateTakeawayTimeSlotEnabled
        && Number(orderType) === Order.numericType.TAKEAWAY) {
        this.setImmediateTakeawayTimeSlotSelection();
        return;
      }

      this.isLoading = true;
      try {
        console.log("Fetching time slot occupancies for orderType:", orderType, "and date:", date);
        const result = await orderService.getTimeSlotOccupancies(orderType, date);
        const rawTimeSlots = result.timeSlots || [];

        if (!result.timeSlots || result.timeSlots.length === 0) {
          this.makeToast("danger", this.$t("notification.noTimeslotsAvailable"));
          return;
        }

        this.syncImmediateOrderDateOption(orderType);

        // Map and set time slots with formatted times
        this.timeSlotOptions = rawTimeSlots.map((slot) => {
          const fromFormatted = formatTimeLocale(slot.fromTime);
          const toFormatted = formatTimeLocale(slot.toTime);

          return {
            value: `${slot.fromTime}-${slot.toTime}`,
            label: this.showOnlyToTime ? toFormatted : `${fromFormatted} - ${toFormatted}`,
          };
        });


        // Preselect the first time slot
        if (this.timeSlotOptions.length > 0) {
          this.selectedTimeSlot = this.timeSlotOptions[0].value;
        }
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      // Find the selected time slot
      const selectedSlot = this.timeSlotOptions.find(
        (slot) => slot.value === this.selectedTimeSlot
      );

      if (!selectedSlot) {
        this.makeToast("danger", this.$t("notification.invalidTimeSlot"));
        return;
      }

      if (this.isImmediateTakeawayTimeSlotSelected(selectedSlot)) {
        await this.startImmediateTakeawayOrderFlow();
        this.$emit('submit');
        return;
      }

      // Extract the time part (e.g., '16:01:00') from the selectedSlot
      const toTime = selectedSlot.value.split('-')[1] || '00:00:00';

      // Parse selectedDate into a Date object
      const date = new Date(this.selectedDate);

      if (isNaN(date)) {
        this.makeToast("danger", this.$t("notification.invalidDate"));
        return;
      }

      // Split the time into hours, minutes, and seconds
      const [hours, minutes, seconds] = toTime.split(':').map((part) => parseInt(part, 10) || 0);

      // Update the Date object with the selected time
      date.setHours(hours, minutes, seconds);

      // Format the resulting Date object into an ISO string or a backend-expected format
      const pickupTime = this.toLocalISOString(date);

      // Call the order service to select the time slot
      orderService.selectTimeSlot(pickupTime);

      const selectedOrderType = getOrderTypeByNumber(this.selectedOrderType);
      this.appStore.setCurrentOrderMenuOrderType(selectedOrderType);

      this.$navigateToOrderMenuPlanner();

      this.$emit('submit');
    },
    toLocalISOString(date) {
      const offset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date - offset);
      return localDate.toISOString().slice(0, -1);
    }
  },
  watch: {
    selectedOrderType(newVal) {
      const previousDate = this.selectedDate;
      this.syncImmediateOrderDateOption(newVal);

      // Wenn beim OrderType-Wechsel das Datum angepasst wurde (z.B. Sofort entfernt),
      // uebernimmt der selectedDate-Watcher das Nachladen der Timeslots.
      if (this.selectedDate !== previousDate) {
        this.$emit('input-change', {
          selectedOrderType: newVal,
          selectedDate: this.selectedDate,
          selectedTimeSlot: this.selectedTimeSlot
        });
        return;
      }

      if (this.isImmediateOrderDateSelected(this.selectedDate)
        && this.immediateTakeawayTimeSlotEnabled
        && Number(newVal) === Order.numericType.TAKEAWAY) {
        this.setImmediateTakeawayTimeSlotSelection();
      }
      // Only fetch if selectedDate is already set
      if (this.selectedDate && !this.isImmediateOrderDateSelected(this.selectedDate)) {
        this.fetchTimeSlotOccupancies(newVal, this.selectedDate);
      }
      this.$emit('input-change', {
        selectedOrderType: newVal,
        selectedDate: this.selectedDate,
        selectedTimeSlot: this.selectedTimeSlot
      });
    },
    selectedDate(newDate) {
      console.log('selectedDate watcher:', newDate, this.selectedOrderType);
      if (this.isImmediateOrderDateSelected(newDate)
        && this.immediateTakeawayTimeSlotEnabled
        && Number(this.selectedOrderType) === Order.numericType.TAKEAWAY) {
        this.setImmediateTakeawayTimeSlotSelection();
      }
      if (newDate && this.selectedOrderType) {
        if (!this.isImmediateOrderDateSelected(newDate)) {
          this.fetchTimeSlotOccupancies(this.selectedOrderType, newDate);
        }
      }
      this.$emit('input-change', {
        selectedOrderType: this.selectedOrderType,
        selectedDate: newDate,
        selectedTimeSlot: this.selectedTimeSlot
      });
    },
    selectedTimeSlot(newSlot) {
      this.$emit('input-change', {
        selectedOrderType: this.selectedOrderType,
        selectedDate: this.selectedDate,
        selectedTimeSlot: newSlot
      });
    }
  },
};
</script>

<style scoped lang="scss">

.orderOptionsTitle {
  font-weight: bold;
  font-size: 20px;
}

.shopSelectionImage{
  height: 200px;
}

#shop {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;

  & > * {
    flex: 1 0 320px;
    //flex-grow: 1;
  }
}
@media screen and (max-width: 768px) {
  #shop{
    flex-direction: column;
    & > * {
      flex: 0; /* reset flex and base */
    }
  }
}

</style>
